import { Assets, Texture } from "pixi.js";
import type { AssetManifest } from "./AssetManifest";

// Manifest values of the form "emoji:<glyph>:<width>x<height>" render a glyph to a canvas instead
// of fetching an image, e.g. "emoji:🪨:40x40" — lets content mix emoji and SVG/PNG art freely.
const EMOJI_PREFIX = "emoji:";

function parseEmojiSpec(value: string): { glyph: string; width: number; height: number } {
  const rest = value.slice(EMOJI_PREFIX.length);
  const sizeStart = rest.lastIndexOf(":");
  const size = rest.slice(sizeStart + 1).split("x").map(Number);
  return { glyph: rest.slice(0, sizeStart), width: size[0] ?? 0, height: size[1] ?? 0 };
}

function renderEmojiTexture(value: string): Texture {
  const { glyph, width, height } = parseEmojiSpec(value);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.font = `${Math.min(width, height) * 0.85}px serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(glyph, width / 2, height / 2 + Math.min(width, height) * 0.05);
  }
  return Texture.from(canvas);
}

/** Lazily loads only the asset keys a scene actually references. Missing/failed assets
 *  fall back to a placeholder texture instead of throwing, so content authoring never
 *  blocks on missing art. Emoji textures are registered into Pixi's own `Assets.cache`
 *  (bypassing the fetch/load pipeline they don't need) so `textureFor` has one lookup path. */
export class AssetLoader {
  constructor(
    private readonly manifest: AssetManifest,
    private readonly baseUrl = "/assets/",
  ) {}

  async loadKeys(keys: readonly string[]): Promise<void> {
    const toLoad = keys.filter((key) => key in this.manifest && !Assets.cache.has(key));
    const toFetch: string[] = [];
    for (const key of toLoad) {
      const value = this.manifest[key]!;
      if (value.startsWith(EMOJI_PREFIX)) {
        Assets.cache.set(key, renderEmojiTexture(value));
      } else {
        Assets.add({ alias: key, src: `${this.baseUrl}${value}` });
        toFetch.push(key);
      }
    }
    await Promise.all(
      toFetch.map((key) =>
        Assets.load(key).catch((error: unknown) => {
          console.warn(`[assets] failed to load "${key}", using placeholder`, error);
        }),
      ),
    );
  }

  async loadExternalTexture(alias: string, src: string): Promise<Texture> {
    if (Assets.cache.has(alias)) return Assets.get<Texture>(alias);
    return Assets.load<Texture>({ alias, src, parser: "texture" });
  }

  textureFor(key: string): { texture: Texture; isPlaceholder: boolean } {
    if (Assets.cache.has(key)) {
      return { texture: Assets.get<Texture>(key), isPlaceholder: false };
    }
    return { texture: Texture.WHITE, isPlaceholder: true };
  }
}
